import type { ActionFunctionArgs } from '~/types/route-args';
import { data as routerData } from 'react-router';
import { z } from 'zod';
import { updateConversationStatus } from '~/modules/chat/api/update-conversation-status.server';

export const UPDATE_CONVERSATION_STATUS_API =
    '/remix-api/chat/update-conversation-status';

const FORM_DATA_SCHEMA = z.object({
    conversationID: z.coerce.number().int().positive(),
    professionalID: z.coerce.number().int().positive().nullish(),
    isImportant: z.coerce.boolean().optional(),
    isArchived: z.coerce.boolean().optional(),
    isDone: z.coerce.boolean().optional(),
});

/**
 * API endpoint to update conversation inbox status flags. Use with the
 * `useUpdateConversation` hook.
 * @see {@link packages/web/app/modules/chat/hooks/use-update-conversation.ts}
 */
export const action = async ({ context, request }: ActionFunctionArgs) => {
    const { logger } = context;
    const payload = await request.json();
    const result = FORM_DATA_SCHEMA.safeParse(payload);

    if (!result.success) {
        return routerData(null, { status: 400 });
    }

    if (
        result.data.isImportant === undefined &&
        result.data.isArchived === undefined &&
        result.data.isDone === undefined
    ) {
        return routerData(null, { status: 400 });
    }

    try {
        const response = await updateConversationStatus({
            context,
            conversationID: result.data.conversationID,
            professionalID: result.data.professionalID,
            isImportant: result.data.isImportant,
            isArchived: result.data.isArchived,
            isDone: result.data.isDone,
        });

        if (!response) {
            return routerData(null, { status: 500 });
        }

        return { conversation: response.conversation };
    } catch (error) {
        logger.error(`Error in ${UPDATE_CONVERSATION_STATUS_API}`, {
            error,
            data: result.data,
        });
        return routerData(null, { status: 500 });
    }
};
